import { CountryFieldProps } from 'components/forms/fields/country-field/country-field.component';
import { useEffect, useMemo, useRef, useState } from 'react';

export type UseCountriesOptions = {
  include?: string[];
  exclude?: string[];
};

export type UseRegionsOptions = {
  countryIdKey?: CountryFieldProps['idKey'];
} & UseCountriesOptions;

type Country = {
  iso: string;
  iso3: string;
  numeric: number;
  fips: string;
  reference: any;
  regions: object[];
  name: string;
};

type Region = {
  name: string;
  iso: string;
  fips: string;
  reference: any;
};

const filter = <T extends Country | Region>(data: T[], include, exclude) => {
  let result = data;
  if (include) {
    result = data.filter(entry => include.includes(entry.iso));
  } else if (exclude) {
    result = data.filter(entry => !exclude.includes(entry.iso));
  }

  return result.sort(sortByName).filter(entry => !!entry.name);
};

const sortByName = (a, b) => (a.name > b.name ? 1 : -1);

export const useCountries = (options: UseCountriesOptions = {}): Country[] => {
  const [data, setData] = useState<{ [key: string]: Country }>();
  const optionsRef = useRef(options);

  useEffect(() => {
    const execute = async () => {
      setData(
        (await import(`iso3166-2-db/countryList/dispute/UN/en.json`)).default,
      );
    };

    execute();
  }, []);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return filter(
      Object.values(data),
      optionsRef.current.include,
      optionsRef.current.exclude,
    );
  }, [data]);

  return filteredData;
};

const useRegionOptionsDefault = {
  include: null,
  exclude: null,
  countryIdKey: 'iso',
};

export const useRegions = (
  countryId: string,
  options?: UseRegionsOptions,
): Region[] => {
  const countries = useCountries();
  const [data, setData] = useState<Region[]>();
  const optionsRef = useRef<UseRegionsOptions>(
    Object.assign({}, useRegionOptionsDefault, options),
  );

  const country = useMemo(
    () =>
      !!countryId && !!optionsRef.current.countryIdKey
        ? countries?.find(
            country => country[optionsRef.current.countryIdKey] === countryId,
          )
        : null,
    [countries, countryId],
  );

  useEffect(() => {
    const execute = async () => {
      setData(
        (
          await import(
            /* webpackMode: "lazy-once" */
            `iso3166-2-db/regions/${country.iso.toUpperCase()}/dispute/UN/en.json`
          )
        ).default,
      );
    };

    if (!!country) {
      execute();
    }
  }, [country]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return filter(data, optionsRef.current.include, optionsRef.current.exclude);
  }, [data]);

  return filteredData;
};
